import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Spinner Component
 * 
 * Production-grade loading spinner:
 * - Size variants (xs, sm, md, lg, xl)
 * - Color variants
 * - Centered option
 * - With label option
 * - Dark mode compatible
 * 
 * @example
 * ```tsx
 * <Spinner size="md" label="Loading..." centered />
 * ```
 */

export interface SpinnerProps {
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Color class (Tailwind) */
  color?: string;
  /** Label text */
  label?: string;
  /** Center in container */
  centered?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'text-blue-600 dark:text-blue-400',
  label,
  centered = false,
  className = '',
}) => {
  // Size classes
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
  };

  // Label size classes
  const labelSizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
  };

  const spinnerElement = (
    <div className={`${centered ? 'flex flex-col items-center justify-center' : 'inline-flex flex-col items-center'} gap-2 ${className}`}>
      <Loader2
        className={`${sizeClasses[size]} ${color} animate-spin`}
        strokeWidth={2.5}
      />
      {label && (
        <span className={`${labelSizeClasses[size]} text-gray-600 dark:text-slate-400`}>
          {label}
        </span>
      )}
    </div>
  );

  if (centered) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[100px]">
        {spinnerElement}
      </div>
    );
  }

  return spinnerElement;
};

Spinner.displayName = 'Spinner';

